import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { RULES } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash, Sparkle, ListChecks, PaperPlaneTilt, Flag, Info, ArrowRight, PencilSimple, EyeSlash, Eye, Flask, ClockCounterClockwise, CheckCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import { RuleActivityEntry } from '@/pages/RuleActivity';
import { Link } from 'react-router-dom';

const TRIGGER_GROUPS = [
    { group: 'When something is added', items: [
        { value: 'new_check_in_answer', label: 'a new check-in answer comes in' },
        { value: 'new_lab_result', label: 'a new lab result is added' },
        { value: 'new_care_note', label: 'a new care note is saved' },
        { value: 'new_medication', label: 'a new medication is added' },
        { value: 'new_family_member', label: 'a new family member is added' },
    ]},
    { group: 'When something changes', items: [
        { value: 'appointment_completed', label: 'an appointment is marked Completed' },
        { value: 'appointment_cancelled', label: 'an appointment is Cancelled' },
        { value: 'medication_stopped', label: 'a medication is marked Stopped' },
    ]},
    { group: 'Time-based', items: [
        { value: 'daily_morning', label: 'every morning (daily check)' },
        { value: 'n_days_before_appointment', label: '[N] days before an appointment' },
        { value: 'check_in_unanswered_n_days', label: 'a check-in hasn\'t been answered [N] days after sent' },
    ]},
];

const TRIGGERS_WITH_N = new Set(['n_days_before_appointment', 'check_in_unanswered_n_days']);
const TRIGGER_LABEL = TRIGGER_GROUPS.reduce((acc, g) => {
    g.items.forEach(i => { acc[i.value] = i.label; });
    return acc;
}, {});

// Which placeholder chips apply for which triggers
const CHIPS_UNIVERSAL = ['[name]', '[first_name]', '[household]', '[today]', '[day_of_week]', '[coordinator]'];
const CHIPS_BY_TRIGGER = {
    new_check_in_answer: ['[value]', '[measure]', '[unit]', '[check_in]', '[previous_value]', '[change]'],
    check_in_unanswered_n_days: ['[check_in]'],
    appointment_completed: ['[appointment]', '[doctor]', '[appointment_date]', '[appointment_time]', '[location]', '[appointment_type]'],
    appointment_cancelled: ['[appointment]', '[doctor]', '[appointment_date]', '[appointment_time]', '[location]', '[appointment_type]'],
    n_days_before_appointment: ['[appointment]', '[doctor]', '[appointment_date]', '[appointment_time]', '[location]', '[appointment_type]', '[days_until]'],
    new_medication: ['[medication]', '[dose]', '[refill_date]', '[prescriber]'],
    medication_stopped: ['[medication]', '[dose]', '[prescriber]'],
    new_lab_result: ['[test]', '[value]', '[unit]', '[normal_range]', '[marker]'],
    new_care_note: ['[summary]', '[days_waiting]'],
    new_family_member: [],
    daily_morning: [],
};

function PlaceholderChips({ trigger, onInsert }) {
    const specific = CHIPS_BY_TRIGGER[trigger] || [];
    const chips = [...CHIPS_UNIVERSAL, ...specific];
    if (chips.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="placeholder-chips">
            <span className="text-[11px] text-slate-500 self-center">Insert:</span>
            {chips.map(c => (
                <button key={c} type="button" onClick={() => onInsert(c)}
                    data-testid={`chip-${c.replace(/[[\]]/g, '')}`}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-mono border border-slate-200">
                    {c}
                </button>
            ))}
        </div>
    );
}

const ASPECTS = [
    { value: 'latest_value', label: 'latest value' },
    { value: 'change_from_previous', label: 'change from previous' },
    { value: 'days_since_last_answer', label: 'days since last answer' },
];
const OPS = [
    { value: '<', label: 'is less than' },
    { value: '<=', label: 'is at most' },
    { value: '>', label: 'is more than' },
    { value: '>=', label: 'is at least' },
    { value: '==', label: 'equals' },
];
const STYLES = ['Celebrate', 'Gentle reminder', 'Heads up'];
const EXPIRES = [
    { value: '24h', label: 'in 24 hours' },
    { value: '48h', label: 'in 48 hours' },
    { value: '1w', label: 'in 1 week' },
    { value: 'replaced', label: 'until replaced' },
];
const STATUS_LABEL = { trying: 'Try it out', on: 'On', off: 'Off' };

function statusPill(status) {
    if (status === 'on') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
    if (status === 'trying') return 'bg-amber-50 text-amber-900 border-amber-200';
    return 'bg-slate-100 text-slate-600 border-slate-200';
}

function RuleSentence({ rule, measures, householdsById, membersById, surveysById }) {
    const measureMap = Object.fromEntries((measures || []).map(m => [m.measure_key, m]));
    const conds = rule.if || [];
    const nSuffix = TRIGGERS_WITH_N.has(rule.when) && rule.n_days != null ? ` (${rule.n_days} days)` : '';
    const whenLabel = (TRIGGER_LABEL[rule.when] || rule.when || 'this event').replace(/\[N\]/g, String(rule.n_days ?? 'N'));
    return (
        <div className="text-sm text-slate-700 leading-relaxed">
            <b>When</b> {whenLabel}{nSuffix && rule.when === 'daily_morning' ? ` ${nSuffix}` : ''}
            {conds.length > 0 && <> <b>and if</b> {conds.map((c, i) => {
                const m = measureMap[c.measure_key];
                const aspect = ASPECTS.find(a => a.value === c.aspect)?.label || c.aspect;
                const op = OPS.find(o => o.value === c.op)?.label || c.op;
                return (
                    <span key={i}>
                        {i > 0 && <> <b>and</b> </>}
                        <span className="font-medium text-slate-900">{m?.display_name || c.measure_display || c.measure_key}</span>
                        <span className="text-slate-500"> · {aspect} · </span>
                        <span>{op} {c.value}</span>
                    </span>
                );
            })}</>}
            {rule.scope === 'household' && householdsById?.[rule.scope_facility_id] && (
                <> <b>for</b> {householdsById[rule.scope_facility_id].name}</>
            )}
            {rule.scope === 'member' && membersById?.[rule.scope_patient_id] && (
                <> <b>for</b> {membersById[rule.scope_patient_id].name}</>
            )}
            <div className="mt-2 pl-4 border-l-2 border-slate-200 space-y-1">
                {(rule.then || []).map((a, i) => (
                    <div key={i} className="flex items-start gap-2">
                        <ArrowRight size={14} className="text-primary mt-0.5" />
                        <div>
                            <b>Then</b> {a.kind === 'add_todo' && <>add a to-do: <span className="font-medium">“{a.config?.title}”</span></>}
                            {a.kind === 'needs_attention' && <>mark it Needs attention: <span className="font-medium">“{a.config?.note}”</span></>}
                            {a.kind === 'send_check_in' && <>send check-in: <span className="font-medium">{surveysById?.[a.config?.survey_id]?.name || 'a check-in'}</span></>}
                            {a.kind === 'post_hero_card' && (
                                <>post a hero card ({a.config?.style || 'Celebrate'}, {EXPIRES.find(e => e.value === a.config?.expires)?.label || 'until replaced'}):
                                    <span className="block mt-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-slate-800">{a.config?.message}</span>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ConditionRow({ cond, measures, onChange, onRemove }) {
    return (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5">
            <Select value={cond.measure_key || 'none'} onValueChange={v => {
                if (v === 'none') return;
                const m = (measures || []).find(mm => mm.measure_key === v);
                onChange({ ...cond, kind: 'measure', measure_key: v, measure_display: m?.display_name || v });
            }}>
                <SelectTrigger data-testid={RULES.condMeasure} className="w-64"><SelectValue placeholder="Pick a measure" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="none">Pick a measure</SelectItem>
                    {(measures || []).map(m => <SelectItem key={m.measure_key} value={m.measure_key}>{m.display_name}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={cond.aspect || 'latest_value'} onValueChange={v => onChange({ ...cond, aspect: v })}>
                <SelectTrigger data-testid={RULES.condAspect} className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>{ASPECTS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={cond.op || '<'} onValueChange={v => onChange({ ...cond, op: v })}>
                <SelectTrigger data-testid={RULES.condOp} className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>{OPS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input
                data-testid={RULES.condValue}
                type="number" step="any" className="w-28"
                value={cond.value ?? ''}
                onChange={e => onChange({ ...cond, value: e.target.value === '' ? null : Number(e.target.value) })}
                placeholder="130"
            />
            <button onClick={onRemove} className="p-1.5 rounded hover:bg-slate-200 text-slate-500 ml-auto"><Trash size={14} /></button>
        </div>
    );
}

function ActionRow({ action, onChange, onRemove, surveys, facilities, trigger }) {
    const insertInto = (field) => (chip) => {
        const current = action.config?.[field] || '';
        const sep = current && !current.endsWith(' ') ? ' ' : '';
        onChange({ ...action, config: { ...action.config, [field]: current + sep + chip } });
    };
    return (
        <div className="rounded-lg border border-slate-200 bg-primary/[0.03] px-3 py-2.5 space-y-2">
            <div className="flex items-center gap-2">
                <Select value={action.kind || 'add_todo'} onValueChange={v => onChange({ kind: v, config: {} })}>
                    <SelectTrigger data-testid={RULES.actionKind} className="w-64"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="add_todo">Add a to-do</SelectItem>
                        <SelectItem value="needs_attention">Mark Needs attention</SelectItem>
                        <SelectItem value="send_check_in">Send a check-in</SelectItem>
                        <SelectItem value="post_hero_card">Post a hero card to the board</SelectItem>
                    </SelectContent>
                </Select>
                <button onClick={onRemove} className="p-1.5 rounded hover:bg-slate-200 text-slate-500 ml-auto"><Trash size={14} /></button>
            </div>
            {action.kind === 'add_todo' && (
                <>
                    <div className="grid grid-cols-[1fr_120px] gap-2">
                        <Input data-testid={RULES.todoTitle} placeholder="Call to check in" value={action.config?.title || ''} onChange={e => onChange({ ...action, config: { ...action.config, title: e.target.value } })} />
                        <div className="flex items-center gap-2">
                            <Input data-testid={RULES.todoDue} type="number" min={0} className="w-20" value={action.config?.due_days_after ?? 3} onChange={e => onChange({ ...action, config: { ...action.config, due_days_after: Number(e.target.value) } })} />
                            <span className="text-xs text-slate-500">days</span>
                        </div>
                    </div>
                    <PlaceholderChips trigger={trigger} onInsert={insertInto('title')} />
                </>
            )}
            {action.kind === 'needs_attention' && (
                <>
                    <Input data-testid={RULES.attnNote} placeholder="What to flag" value={action.config?.note || ''} onChange={e => onChange({ ...action, config: { ...action.config, note: e.target.value } })} />
                    <PlaceholderChips trigger={trigger} onInsert={insertInto('note')} />
                </>
            )}
            {action.kind === 'send_check_in' && (
                <Select value={action.config?.survey_id || 'none'} onValueChange={v => onChange({ ...action, config: { survey_id: v === 'none' ? null : v } })}>
                    <SelectTrigger data-testid={RULES.sendSurvey}><SelectValue placeholder="Choose a check-in" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="none">Choose a check-in</SelectItem>
                        {(surveys || []).map(s => <SelectItem key={s.survey_id} value={s.survey_id}>{s.name}</SelectItem>)}
                    </SelectContent>
                </Select>
            )}
            {action.kind === 'post_hero_card' && (
                <div className="space-y-2">
                    <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                        <Info size={14} className="mt-0.5 shrink-0" />
                        Anyone who can see this board can read this — keep it warm, keep details private.
                    </div>
                    <Textarea
                        data-testid={RULES.heroMessage}
                        rows={2}
                        placeholder="Way to go, [name]! Your numbers looked great today 💚"
                        value={action.config?.message || ''}
                        onChange={e => onChange({ ...action, config: { ...action.config, message: e.target.value } })}
                    />
                    <PlaceholderChips trigger={trigger} onInsert={insertInto('message')} />
                    <div className="grid grid-cols-3 gap-2">
                        <Select value={action.config?.style || 'Celebrate'} onValueChange={v => onChange({ ...action, config: { ...action.config, style: v } })}>
                            <SelectTrigger data-testid={RULES.heroStyle}><SelectValue /></SelectTrigger>
                            <SelectContent>{STYLES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={action.config?.expires || '48h'} onValueChange={v => onChange({ ...action, config: { ...action.config, expires: v } })}>
                            <SelectTrigger data-testid={RULES.heroExpires}><SelectValue /></SelectTrigger>
                            <SelectContent>{EXPIRES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={action.config?.facility_id || 'auto'} onValueChange={v => onChange({ ...action, config: { ...action.config, facility_id: v } })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="auto">Household of triggering member</SelectItem>
                                {(facilities || []).map(f => <SelectItem key={f.facility_id} value={f.facility_id}>{f.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            )}
        </div>
    );
}

function RuleEditor({ initial, onClose, onSaved }) {
    const isEdit = !!initial?.rule_id;
    const [form, setForm] = useState(() => ({
        name: initial?.name || 'New rule',
        when: initial?.when || 'new_check_in_answer',
        n_days: initial?.n_days ?? 3,
        scope: initial?.scope || 'all',
        scope_facility_id: initial?.scope_facility_id || null,
        scope_patient_id: initial?.scope_patient_id || null,
        if_conditions: initial?.if || [],
        then_actions: initial?.then || [{ kind: 'post_hero_card', config: { style: 'Celebrate', expires: '48h', message: '', facility_id: 'auto' } }],
        status: initial?.status || 'trying',
    }));
    const { data: measures = [] } = useQuery({ queryKey: ['rules-measures'], queryFn: async () => (await api.get('/rules-measures')).data });
    const { data: surveys = [] } = useQuery({ queryKey: ['surveys'], queryFn: async () => (await api.get('/surveys')).data });
    const { data: facilities = [] } = useQuery({ queryKey: ['facilities'], queryFn: async () => (await api.get('/facilities')).data });
    const [membersOfHH, setMembersOfHH] = useState([]);
    React.useEffect(() => {
        if (form.scope === 'member' && form.scope_facility_id) {
            api.get('/patients', { params: { facility_id: form.scope_facility_id } }).then(r => setMembersOfHH(r.data));
        } else { setMembersOfHH([]); }
    }, [form.scope, form.scope_facility_id]);

    const patch = (u) => setForm(prev => ({ ...prev, ...u }));
    const patchCond = (i, next) => patch({ if_conditions: form.if_conditions.map((c, idx) => idx === i ? next : c) });
    const patchAction = (i, next) => patch({ then_actions: form.then_actions.map((a, idx) => idx === i ? next : a) });

    const save = async () => {
        if (!form.name.trim()) { toast.error('Give it a short name'); return; }
        if (form.then_actions.length === 0) { toast.error('Add at least one Then action'); return; }
        const payload = {
            name: form.name,
            when: form.when,
            n_days: TRIGGERS_WITH_N.has(form.when) ? Number(form.n_days) || 0 : null,
            scope: form.scope,
            scope_facility_id: form.scope === 'household' || form.scope === 'member' ? form.scope_facility_id : null,
            scope_patient_id: form.scope === 'member' ? form.scope_patient_id : null,
            if_conditions: form.if_conditions,
            then_actions: form.then_actions,
            status: form.status,
        };
        try {
            if (isEdit) await api.put(`/rules/${initial.rule_id}`, payload);
            else await api.post('/rules', payload);
            toast.success('Saved');
            onSaved?.(); onClose?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    };

    return (
        <div className="space-y-5">
            <div>
                <Label>Name (just for you)</Label>
                <Input data-testid={RULES.nameInput} value={form.name} onChange={e => patch({ name: e.target.value })} />
            </div>

            <div>
                <Label>When…</Label>
                <div className="flex flex-wrap items-center gap-2">
                    <Select value={form.when} onValueChange={v => patch({ when: v })}>
                        <SelectTrigger data-testid="rule-when-select" className="w-[380px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {TRIGGER_GROUPS.map(g => (
                                <React.Fragment key={g.group}>
                                    <div className="px-2 pt-1.5 pb-1 text-[10px] uppercase tracking-widest text-slate-500">{g.group}</div>
                                    {g.items.map(i => (
                                        <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                                    ))}
                                </React.Fragment>
                            ))}
                        </SelectContent>
                    </Select>
                    {TRIGGERS_WITH_N.has(form.when) && (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">N =</span>
                            <Input data-testid="rule-n-days" type="number" min={0} className="w-20"
                                value={form.n_days ?? 3}
                                onChange={e => patch({ n_days: Number(e.target.value) })} />
                            <span className="text-xs text-slate-500">days</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-[120px_1fr_1fr] gap-2 items-end">
                <div>
                    <Label>Applies to</Label>
                    <Select value={form.scope} onValueChange={v => patch({ scope: v, scope_facility_id: null, scope_patient_id: null })}>
                        <SelectTrigger data-testid={RULES.scopeSelect}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Everyone</SelectItem>
                            <SelectItem value="household">One household</SelectItem>
                            <SelectItem value="member">One family member</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {(form.scope === 'household' || form.scope === 'member') && (
                    <div>
                        <Label>Household</Label>
                        <Select value={form.scope_facility_id || 'none'} onValueChange={v => patch({ scope_facility_id: v === 'none' ? null : v, scope_patient_id: null })}>
                            <SelectTrigger data-testid={RULES.scopeHouseholdSelect}><SelectValue placeholder="Choose household" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Choose household</SelectItem>
                                {facilities.map(f => <SelectItem key={f.facility_id} value={f.facility_id}>{f.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                {form.scope === 'member' && (
                    <div>
                        <Label>Family member</Label>
                        <Select value={form.scope_patient_id || 'none'} onValueChange={v => patch({ scope_patient_id: v === 'none' ? null : v })}>
                            <SelectTrigger data-testid={RULES.scopeMemberSelect}><SelectValue placeholder="Choose family member" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Choose family member</SelectItem>
                                {membersOfHH.map(m => <SelectItem key={m.patient_id} value={m.patient_id}>{m.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                )}
            </div>

            <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{TRIGGER_LABEL[form.when] || 'This event'} <b className="normal-case tracking-normal">and if…</b></div>
                <div className="space-y-2">
                    {form.if_conditions.map((c, i) => (
                        <ConditionRow key={i} cond={c} measures={measures}
                            onChange={next => patchCond(i, next)}
                            onRemove={() => patch({ if_conditions: form.if_conditions.filter((_, idx) => idx !== i) })} />
                    ))}
                    <Button variant="outline" size="sm" data-testid={RULES.addCondition}
                        onClick={() => patch({ if_conditions: [...form.if_conditions, { kind: 'measure', aspect: 'latest_value', op: '<', value: 130 }] })}
                        className="rounded-full">
                        <Plus size={14} className="mr-1" /> Add condition
                    </Button>
                    {form.if_conditions.length === 0 && (
                        <div className="text-xs text-slate-500">No condition — the rule will trigger on every new check-in answer.</div>
                    )}
                </div>
            </div>

            <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Then…</div>
                <div className="space-y-2">
                    {form.then_actions.map((a, i) => (
                        <ActionRow key={i} action={a}
                            onChange={next => patchAction(i, next)}
                            onRemove={() => patch({ then_actions: form.then_actions.filter((_, idx) => idx !== i) })}
                            surveys={surveys} facilities={facilities} trigger={form.when} />
                    ))}
                    <Button variant="outline" size="sm" data-testid={RULES.addAction}
                        onClick={() => patch({ then_actions: [...form.then_actions, { kind: 'add_todo', config: { title: '', due_days_after: 3 } }] })}
                        className="rounded-full">
                        <Plus size={14} className="mr-1" /> Add action
                    </Button>
                </div>
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => patch({ status: v })}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="trying">Try it out (dry-run)</SelectItem>
                        <SelectItem value="on">On</SelectItem>
                        <SelectItem value="off">Off</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <DialogFooter>
                <Button data-testid={RULES.saveButton} onClick={save} className="rounded-full bg-primary text-primary-foreground">Save rule</Button>
            </DialogFooter>
        </div>
    );
}

function TestRuleDialog({ rule, measures }) {
    const [open, setOpen] = useState(false);
    const [patientId, setPatientId] = useState('');
    const [values, setValues] = useState({});
    const [bpSys, setBpSys] = useState('');
    const [bpDia, setBpDia] = useState('');
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const { data: facilities = [] } = useQuery({ queryKey: ['facilities'], queryFn: async () => (await api.get('/facilities')).data, enabled: open });
    const facIds = facilities.map(f => f.facility_id);
    const { data: allPatients = [] } = useQuery({
        queryKey: ['all-patients', facIds.join(',')],
        queryFn: async () => {
            const lists = await Promise.all(facIds.map(id => api.get('/patients', { params: { facility_id: id } }).then(r => r.data)));
            return lists.flat();
        },
        enabled: open && facIds.length > 0,
    });

    const uniqueMeasureKeys = React.useMemo(() => {
        const keys = new Set();
        (rule.if || []).forEach(c => { if (c.measure_key) keys.add(c.measure_key); });
        return Array.from(keys);
    }, [rule]);
    const measureMap = Object.fromEntries((measures || []).map(m => [m.measure_key, m]));

    const run = async () => {
        if (!patientId) { toast.error('Pick a family member'); return; }
        const pretend = { ...values };
        // For BP top/bottom shortcut, map to both if user set either
        if (bpSys !== '') { pretend.bp_systolic = Number(bpSys); }
        if (bpDia !== '') { pretend.bp_diastolic = Number(bpDia); }
        setBusy(true);
        try {
            const payload = { patient_id: patientId, pretend_values: pretend };
            if (bpSys !== '' && bpDia !== '') payload.trigger_bp = { systolic: Number(bpSys), diastolic: Number(bpDia) };
            const { data } = await api.post(`/rules/${rule.rule_id}/test`, payload);
            setResult(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Test failed');
        } finally { setBusy(false); }
    };
    const reset = () => { setResult(null); setValues({}); setBpSys(''); setBpDia(''); };

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setResult(null); } }}>
            <DialogTrigger asChild>
                <Button size="sm" variant="ghost" data-testid={`rules-test-${rule.rule_id}`} className="rounded-full">
                    <Flask size={14} className="mr-1" /> Test
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">Test this rule</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-slate-600">Pick a family member and enter a pretend answer for each measure this rule checks. Nothing real is created.</div>
                    <div>
                        <Label>Family member</Label>
                        <Select value={patientId || 'none'} onValueChange={v => setPatientId(v === 'none' ? '' : v)}>
                            <SelectTrigger data-testid="rules-test-patient"><SelectValue placeholder="Choose family member" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">Choose family member</SelectItem>
                                {allPatients.map(p => <SelectItem key={p.patient_id} value={p.patient_id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {uniqueMeasureKeys.map(k => {
                        const m = measureMap[k];
                        const label = m?.display_name || k;
                        // Special: for bp_systolic + bp_diastolic show top/bottom pair
                        if (k === 'bp_systolic') {
                            return (
                                <div key={k}>
                                    <Label>{label}</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <Input data-testid="rules-test-bp-sys" type="number" placeholder="Top e.g. 152" value={bpSys} onChange={e => setBpSys(e.target.value)} />
                                        <Input data-testid="rules-test-bp-dia" type="number" placeholder="Bottom e.g. 96" value={bpDia} onChange={e => setBpDia(e.target.value)} />
                                    </div>
                                </div>
                            );
                        }
                        if (k === 'bp_diastolic') return null; // covered by pair above if present
                        return (
                            <div key={k}>
                                <Label>{label} {m?.unit && <span className="text-slate-500 text-xs">({m.unit})</span>}</Label>
                                <Input
                                    data-testid={`rules-test-value-${k}`}
                                    type="number" step="any"
                                    value={values[k] ?? ''}
                                    onChange={e => setValues({ ...values, [k]: e.target.value === '' ? null : Number(e.target.value) })}
                                    placeholder="Pretend value"
                                />
                            </div>
                        );
                    })}
                    {uniqueMeasureKeys.length === 0 && (
                        <div className="text-xs text-slate-500">This rule has no measure conditions yet — testing will just check scope.</div>
                    )}
                    <div className="flex items-center gap-2">
                        <Button data-testid="rules-test-run" onClick={run} disabled={busy || !patientId} className="rounded-full bg-primary text-primary-foreground">
                            {busy ? 'Running…' : 'Run test'}
                        </Button>
                        {result && (
                            <Button onClick={reset} variant="ghost" size="sm" className="rounded-full">Clear</Button>
                        )}
                    </div>

                    {result && (
                        <div data-testid="rules-test-result" className="rounded-xl border border-sky-200 bg-sky-50/70 p-4 space-y-3">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-sky-900">
                                <Flask size={12} weight="duotone" /> This is a test — nothing was actually created.
                            </div>
                            <div className="flex items-start gap-2 text-sm">
                                {result.matched
                                    ? <><CheckCircle size={18} weight="duotone" className="text-emerald-700 mt-0.5" /><div><b>Matched.</b> {result.reason}</div></>
                                    : <><XCircle size={18} weight="duotone" className="text-slate-500 mt-0.5" /><div><b>Did not match.</b> {result.reason}</div></>}
                            </div>
                            <ul className="space-y-1 pl-1">
                                {(result.condition_traces || []).map((t, i) => (
                                    <li key={i} className="text-xs text-slate-700 flex items-start gap-1.5">
                                        <span className={`mt-1 h-1.5 w-1.5 rounded-full ${t.matched ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                                        <span>{t.reason}</span>
                                    </li>
                                ))}
                            </ul>
                            {(result.action_previews || []).length > 0 && (
                                <div className="pt-2 border-t border-sky-200">
                                    <div className="text-[11px] uppercase tracking-widest text-slate-600 mb-1">Would run</div>
                                    <div className="space-y-2">
                                        {result.action_previews.map((a, i) => {
                                            const p = a.preview || {};
                                            if (a.kind === 'add_todo') return <div key={i} className="rounded-md border border-slate-200 bg-white/80 px-3 py-2 text-xs">➕ Add to-do: <b>“{p.title}”</b></div>;
                                            if (a.kind === 'needs_attention') return <div key={i} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs">⚑ Needs attention: <b>“{p.note}”</b></div>;
                                            if (a.kind === 'send_check_in') return <div key={i} className="rounded-md border border-slate-200 bg-white/80 px-3 py-2 text-xs">✉ Send a check-in</div>;
                                            if (a.kind === 'post_hero_card') return (
                                                <div key={i} className="rounded-md border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm">
                                                    <div className="text-[10px] uppercase tracking-widest text-amber-900">Hero card · {p.style}</div>
                                                    <div className="mt-1 text-slate-900 whitespace-pre-wrap">{p.message}</div>
                                                </div>
                                            );
                                            return <div key={i} className="text-xs">{a.kind}</div>;
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function RuleActivityDialog({ rule }) {
    const [open, setOpen] = useState(false);
    const { data: entries = [] } = useQuery({
        queryKey: ['rule-activity-per', rule.rule_id, open],
        queryFn: async () => (await api.get(`/rules/${rule.rule_id}/activity`)).data,
        enabled: open,
    });
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" variant="ghost" data-testid={`rules-activity-${rule.rule_id}`} className="rounded-full">
                    <ClockCounterClockwise size={14} className="mr-1" /> Activity
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle className="font-heading text-2xl">Activity · {rule.name}</DialogTitle></DialogHeader>
                {entries.length === 0 ? (
                    <div className="text-sm text-slate-500 py-8 text-center">No runs yet.</div>
                ) : (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                        {entries.map(e => <RuleActivityEntry key={e.activity_id} entry={e} />)}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function Reminders() {
    const qc = useQueryClient();
    const { canMutateAdmin } = useAuth();
    const { data: rules = [] } = useQuery({ queryKey: ['rules'], queryFn: async () => (await api.get('/rules')).data });
    const { data: measures = [] } = useQuery({ queryKey: ['rules-measures'], queryFn: async () => (await api.get('/rules-measures')).data });
    const { data: facilities = [] } = useQuery({ queryKey: ['facilities'], queryFn: async () => (await api.get('/facilities')).data });
    const { data: surveys = [] } = useQuery({ queryKey: ['surveys'], queryFn: async () => (await api.get('/surveys')).data });
    const [addOpen, setAddOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [del, setDel] = useState(null);

    const householdsById = useMemo(() => Object.fromEntries(facilities.map(f => [f.facility_id, f])), [facilities]);
    const surveysById = useMemo(() => Object.fromEntries(surveys.map(s => [s.survey_id, s])), [surveys]);

    const invalidate = () => qc.invalidateQueries({ queryKey: ['rules'] });
    const setStatus = async (r, status) => {
        try { await api.post(`/rules/${r.rule_id}/status`, { status }); toast.success(`Rule ${STATUS_LABEL[status]}`); invalidate(); }
        catch { toast.error('Failed'); }
    };
    const remove = async () => {
        try { await api.delete(`/rules/${del.rule_id}`); toast.success('Removed'); setDel(null); invalidate(); }
        catch { toast.error('Failed'); }
    };

    return (
        <div data-testid={RULES.root} className="px-6 md:px-10 py-8 max-w-5xl">
            <header className="mb-6 flex items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                        <ListChecks size={12} weight="duotone" className="text-primary" /> Reminders & Rules
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 mt-1">Little rules, gentle nudges</h1>
                    <p className="text-slate-600 mt-2 max-w-2xl text-sm">
                        Build a rule as a plain-language sentence. Start in <b>Try it out</b> mode to preview what would happen, then flip it On when you’re ready.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button asChild variant="outline" className="rounded-full">
                        <Link data-testid="rules-view-activity" to="/reminders/activity"><ClockCounterClockwise size={14} className="mr-1" /> View activity</Link>
                    </Button>
                    {canMutateAdmin && (
                        <Dialog open={addOpen} onOpenChange={setAddOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid={RULES.addButton} className="rounded-full bg-primary text-primary-foreground">
                                    <Plus size={16} className="mr-1" /> New rule
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                                <DialogHeader><DialogTitle className="font-heading text-2xl">New rule</DialogTitle></DialogHeader>
                                <RuleEditor onClose={() => setAddOpen(false)} onSaved={invalidate} />
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </header>

            {rules.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-500">
                    <Sparkle size={28} className="mx-auto text-slate-400 mb-2" />
                    No rules yet. Try one that celebrates a great blood pressure reading.
                </div>
            ) : (
                <div className="space-y-3">
                    {rules.map(r => {
                        const membersById = {};  // we don't lazy-fetch members here; RuleSentence handles missing
                        return (
                            <div key={r.rule_id} data-testid={RULES.row} className="rounded-xl border border-slate-200 bg-card px-5 py-4 shadow-sm">
                                <div className="flex items-center justify-between gap-3 mb-2">
                                    <div className="flex items-center gap-2">
                                        <div className="font-heading text-xl text-slate-900">{r.name}</div>
                                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${statusPill(r.status)}`}>
                                            {STATUS_LABEL[r.status] || r.status}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <TestRuleDialog rule={r} measures={measures} />
                                        <RuleActivityDialog rule={r} />
                                        {canMutateAdmin && (
                                            <>
                                                <Button size="sm" variant="ghost" data-testid={`${RULES.tryItToggle}-${r.rule_id}`} onClick={() => setStatus(r, r.status === 'on' ? 'trying' : 'on')} className="rounded-full">
                                                    {r.status === 'on' ? <><EyeSlash size={14} className="mr-1" /> Switch to Try it out</> : <><Eye size={14} className="mr-1" /> Turn on</>}
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => setEdit(r)} className="rounded-full"><PencilSimple size={14} /></Button>
                                                <Button size="sm" variant="ghost" data-testid={RULES.delete} onClick={() => setDel(r)} className="rounded-full text-red-600"><Trash size={14} /></Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <RuleSentence rule={r} measures={measures} householdsById={householdsById} membersById={membersById} surveysById={surveysById} />
                                {r.last_fire && (
                                    <div className={`mt-3 text-[11px] rounded-md px-3 py-1.5 border ${r.last_fire.dry_run ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
                                        {r.last_fire.dry_run ? 'Try-it-out preview' : 'Fired'} · {new Date(r.last_fire.fired_at).toLocaleString()}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <Dialog open={!!edit} onOpenChange={v => !v && setEdit(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Edit rule</DialogTitle></DialogHeader>
                    {edit && <RuleEditor initial={edit} onClose={() => setEdit(null)} onSaved={invalidate} />}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={!!del} onOpenChange={v => !v && setDel(null)}
                title="Delete this rule?"
                description={del ? `"${del.name}" will stop firing. Any hero cards it already posted stay on the board.` : ''}
                onConfirm={remove}
            />
        </div>
    );
}
